import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

import { ChevronRight, Phone, Globe, MapPin } from "lucide-react";

import ProfesoresList from "./ProfesoresList";
import KinderProfesoresList from "./KinderProfesoresList";
import GradosCDA from "./GradosCDA";
import HorariosProfesor from "./HorariosProfesor";
import ReservaCita from "./ReservaCita";
import MisCitasPadre from "./MisCitasPadre";
import SelectorIdioma from "./SelectorIdioma";
import SidebarShell from "./SidebarShell";
import { useIdioma } from "../context/IdiomaContext";
import { useEstilo, useEstiloServicios } from "../context/EstiloContext";
import { getFondoServicio, armarFondo } from "../utils/fondoServicio";
import { colorTextoLegible } from "../utils/contraste";

/* 🔴 MUY IMPORTANTE — URL GLOBAL DEL BACKEND */

//const API = "http://localhost:5000";
const API = process.env.REACT_APP_API_URL || "";

const PadrePanel = ({ usuario, onLogout, colorFondo = "#f0f4ff", colorHeader = "#1e40af" }) => {
  const { t } = useIdioma();
  const estilo = useEstiloServicios();
  const estiloGlobal = useEstilo();
  const [vista, setVista] = useState("servicios");
  const [profesorSeleccionado, setProfesorSeleccionado] = useState(null);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [servicioActual, setServicioActual] = useState(null);
  const [cargandoServicios, setCargandoServicios] = useState(true);

  /* ================= SCROLL ARRIBA EN CADA PASO ================= */
  useEffect(() => {
    // "horarios" centra su propio calendario (ver HorariosProfesor.js)
    if (vista !== "horarios") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [vista]);

  /* ================= HISTORIAL DE NAVEGACIÓN LIGADO AL NAVEGADOR =================
     Antes, cada pantalla (servicio → profesor → horario → reserva) era solo
     estado de React, sin ninguna relación con el historial real del navegador
     — por eso la flecha "atrás"/"adelante" no hacía nada (o sacaba al usuario
     del sitio). Ahora cada avance de pantalla guarda una "foto" del estado
     (pilaRef) y agrega una entrada real al historial (pushState); "atrás" y
     "adelante" del navegador disparan popstate, que restaura la foto
     correspondiente — igual a como se movería el propio botón "Regresar". */
  const pilaRef = useRef([
    { vista: "servicios", profesorSeleccionado: null, horarioSeleccionado: null, servicioActual: null },
  ]);
  const indiceRef = useRef(0);

  const aplicarSnapshot = (snap) => {
    setVista(snap.vista);
    setProfesorSeleccionado(snap.profesorSeleccionado);
    setHorarioSeleccionado(snap.horarioSeleccionado);
    setServicioActual(snap.servicioActual);
  };

  useEffect(() => {
    // Marca la entrada de historial ya cargada (paso 0) para que el primer
    // "atrás" también sea reconocido por nuestra pila, en vez de quedar en
    // una entrada sin estado que no restaura nada.
    window.history.replaceState({ paso: 0 }, "", window.location.href);

    const alDetectarPopState = (e) => {
      const paso = e.state?.paso;
      const snap = typeof paso === "number" ? pilaRef.current[paso] : undefined;
      if (!snap) return; // fuera de nuestra pila: dejar que el navegador siga su curso normal
      indiceRef.current = paso;
      aplicarSnapshot(snap);
    };
    window.addEventListener("popstate", alDetectarPopState);
    return () => window.removeEventListener("popstate", alDetectarPopState);
  }, []);

  // Avanza a una nueva pantalla, agregando un paso real al historial del navegador.
  const irA = (cambios) => {
    const siguiente = { ...pilaRef.current[indiceRef.current], ...cambios };
    pilaRef.current = [...pilaRef.current.slice(0, indiceRef.current + 1), siguiente];
    indiceRef.current += 1;
    window.history.pushState({ paso: indiceRef.current }, "", window.location.href);
    aplicarSnapshot(siguiente);
  };

  /* ================= VOLVER A LA LISTA DE PROFESORES DEL SERVICIO ================= */
  const volverAListaProfesores = () => {
    let vistaDestino = "servicios";
    switch (servicioActual?.ruta) {
      case "/citas-colegio-2026": vistaDestino = "colegio"; break;
      case "/citas-cda-2026": vistaDestino = "cda"; break;
      case "/citas-kindergarten-2026": vistaDestino = "kindergarten"; break;
      default: vistaDestino = "servicios";
    }
    irA({ vista: vistaDestino, profesorSeleccionado: null, horarioSeleccionado: null });
  };

  // El botón "Regresar" de la app usa el mismo mecanismo que el botón físico
  // del navegador, para que ambos se muevan siempre por la misma pila.
  const volver = () => {
    window.history.back();
  };

  /* ================= PANTALLA DE INICIO (solo estilo "Portal Lateral") ================= */
  // El estilo carga de forma asíncrona (empieza en "clásico" mientras
  // llega la respuesta del servidor), por eso este efecto lo detecta en
  // cuanto queda disponible y recién ahí manda al padre a "Inicio". No es
  // una navegación del usuario, así que solo actualiza la foto actual de la
  // pila (sin agregar un paso nuevo al historial del navegador).
  useEffect(() => {
    if (estiloGlobal.tieneInicio) {
      setVista("inicio");
      const actual = pilaRef.current[indiceRef.current];
      if (actual) {
        pilaRef.current[indiceRef.current] = { ...actual, vista: "inicio" };
      }
    }
  }, [estiloGlobal.tieneInicio]);

  /* ================= NAVEGACIÓN DESDE EL MENÚ LATERAL ================= */
  const manejarNavSidebar = (id) => {
    if (id === "inicio" || id === "servicios") {
      irA({ vista: id, profesorSeleccionado: null, horarioSeleccionado: null });
    } else {
      irA({ vista: id });
    }
  };

  /* ================= TRAER SERVICIOS ================= */
  useEffect(() => {
    const fetchServicios = async () => {
      try {
        setCargandoServicios(true);

        const res = await axios.get(`${API}/api/servicios`);

        if (!Array.isArray(res.data)) {
          console.error("Servicios no es array:", res.data);
          return;
        }

        const habilitados = res.data.filter((s) => s.habilitado === 1);
        setServicios(habilitados);

      } catch (err) {
        console.error("Error cargando servicios:", err.message);
        alert("No se pudo conectar con el servidor.");
      } finally {
        setCargandoServicios(false);
      }
    };

    fetchServicios();
  }, []);

  /* ================= CLICK SERVICIO ================= */
  const manejarClickServicio = (servicio) => {
    if (!servicio?.ruta) {
      alert("Servicio sin ruta configurada");
      return;
    }

    switch (servicio.ruta) {
      case "/citas-colegio-2026":
        irA({ vista: "colegio", servicioActual: servicio });
        break;
      case "/citas-cda-2026":
        irA({ vista: "cda", servicioActual: servicio });
        break;
      case "/citas-kindergarten-2026":
        irA({ vista: "kindergarten", servicioActual: servicio });
        break;
      default:
        alert("Servicio no configurado");
    }
  };

  /* ================= HEADER ================= */
  // El fondo de la página en este momento (el del servicio elegido, o el
  // color global si todavía no se elige ninguno — "Seleccione un Servicio").
  // Si ESE fondo es un degradado, el encabezado se vuelve transparente para
  // dejarlo ver por debajo, sin costura. Si es sólido, usa el color de
  // encabezado normal configurado en "Colores del Sitio".
  const fondoActual = getFondoServicio(servicioActual, colorFondo);
  const colorHeaderEfectivo =
    typeof fondoActual === "string" && fondoActual.startsWith("linear-gradient")
      ? "transparent"
      : colorHeader;

  const Header = () => (
    <>
    <header
      className="padre-header"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "10px",
        padding: "12px 16px",
        background: colorHeaderEfectivo,
        color: "white",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <div
        className="padre-header-info"
        style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          className="padre-header-logo"
          style={{ height: "40px", flexShrink: 0 }}
        />
        <div style={{ minWidth: 0, overflow: "hidden" }}>
          <strong
            className="padre-header-title"
            style={{ fontSize: "18px", whiteSpace: "nowrap" }}
          >
            {t("panel_titulo")}
          </strong>
          <div
            className="padre-header-sub"
            style={{
              fontSize: "13px",
              opacity: 0.9,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("panel_bienvenido")}, {usuario?.nombre || "Usuario"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: "8px", flexShrink: 0 }}>
        <SelectorIdioma />
        {/* "Regresar" no tiene sentido en la pantalla principal (Seleccione un
            Servicio) — no hay a dónde volver, por eso solo aparece en las demás */}
        {vista !== "servicios" && (
          <button onClick={volver} style={btnVolver}>{t("panel_regresar")}</button>
        )}
        <button onClick={onLogout} style={btnSalir}>{t("panel_cerrar_sesion")}</button>
      </div>
    </header>

    {/* Saludo aparte, fuera de la barra fija — solo se muestra en
        celular, ya que ahí no cabe junto al logo y los botones */}
    <div className="padre-mobile-greeting">
      {t("panel_bienvenido")}, {usuario?.nombre || "Usuario"}
    </div>

      <style>
        {`
          @media (max-width: 480px) {
            .padre-header {
              padding: 8px 10px !important;
              gap: 6px !important;
            }
            .padre-header-logo {
              height: 26px !important;
            }
            .padre-header-title {
              font-size: 13px !important;
            }
            .padre-header-sub {
              display: none !important;
            }
            .padre-mobile-greeting {
              display: block !important;
            }
          }
          .padre-mobile-greeting {
            display: none;
            background: ${colorHeaderEfectivo};
            color: white;
            font-size: 12px;
            padding: 6px 14px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}
      </style>
    </>
  );

  /* ================= ENVOLTURA: BARRA SUPERIOR vs MENÚ LATERAL ================= */
  const Envolver = ({ children, fondo }) => {
    if (estiloGlobal.shell === "sidebar") {
      return (
        <SidebarShell
          usuario={usuario}
          onLogout={onLogout}
          vistaActual={vista}
          onNavegar={manejarNavSidebar}
          onVolver={vista !== "servicios" && vista !== "inicio" ? volver : null}
          colorAcento={colorHeader}
          fondo={fondo}
        >
          {children}
        </SidebarShell>
      );
    }
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: fondo }}>
        <Header />
        {children}
      </div>
    );
  };

  const btnSalir = {
    padding: "6px 14px",
    backgroundColor: "#ef4444",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "white",
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
  };

  const btnVolver = {
    backgroundColor: "rgba(255,255,255,0.15)",
    color: "white",
    border: "none",
    padding: "6px 14px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "bold",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  /* ================= PROTECCIÓN ================= */
  if (!usuario) {
    return <div style={{ minHeight: "100vh", background: colorFondo }}>{t("panel_cargando_usuario")}</div>;
  }

  /* ================= VISTAS ================= */

  if (vista === "inicio")
    return (
      <Envolver fondo={colorFondo}>
        <div className="portal-anclado" style={{ maxWidth: 720, margin: "20px auto 0", textAlign: "center" }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6, color: colorTextoLegible(colorFondo) }}>
            {t("portal_bienvenido")}, {usuario?.nombre || "Usuario"}!
          </h2>
          <p style={{ fontSize: 15, opacity: 0.8, marginBottom: 30, color: colorTextoLegible(colorFondo) }}>
            {t("portal_que_deseas")}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 18, justifyContent: "center", marginBottom: 24 }}>
            <div
              onClick={() => manejarNavSidebar("servicios")}
              style={{
                flex: "1 1 280px",
                maxWidth: 320,
                textAlign: "left",
                background: "#ffffff",
                borderRadius: estiloGlobal.cardRadius,
                boxShadow: estiloGlobal.cardShadow,
                border: estiloGlobal.cardBorder,
                padding: 22,
                cursor: "pointer",
              }}
            >
              <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111827" }}>
                {t("portal_agendar_titulo")}
              </h3>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
                {t("portal_agendar_desc")}
              </p>
              <button
                style={{
                  background: colorHeader,
                  color: "white",
                  border: "none",
                  borderRadius: estiloGlobal.botonRadius,
                  padding: "9px 18px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {t("portal_agendar_btn")}
              </button>
            </div>

            <div
              onClick={() => manejarNavSidebar("misCitas")}
              style={{
                flex: "1 1 280px",
                maxWidth: 320,
                textAlign: "left",
                background: "#ffffff",
                borderRadius: estiloGlobal.cardRadius,
                boxShadow: estiloGlobal.cardShadow,
                border: estiloGlobal.cardBorder,
                padding: 22,
                cursor: "pointer",
              }}
            >
              <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111827" }}>
                {t("portal_vercitas_titulo")}
              </h3>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
                {t("portal_vercitas_desc")}
              </p>
              <button
                style={{
                  background: "white",
                  color: colorHeader,
                  border: `2px solid ${colorHeader}`,
                  borderRadius: estiloGlobal.botonRadius,
                  padding: "9px 18px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {t("portal_vercitas_btn")}
              </button>
            </div>
          </div>

          <div
            style={{
              textAlign: "left",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 12,
              padding: "14px 18px",
            }}
          >
            <strong style={{ fontSize: 13, color: "#92400e" }}>{t("portal_aviso_titulo")}</strong>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#92400e" }}>{t("portal_aviso_texto")}</p>
          </div>
        </div>

        <style>
          {`
            @media (min-width: 861px) {
              .portal-anclado {
                transform: translateX(-130px);
              }
            }
          `}
        </style>
      </Envolver>
    );

  if (vista === "ayuda")
    return (
      <Envolver fondo={colorFondo}>
        <div className="portal-anclado" style={{ maxWidth: 480, margin: "20px auto 0" }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, color: colorTextoLegible(colorFondo) }}>
            {t("portal_ayuda_titulo")}
          </h2>
          <p style={{ fontSize: 14, opacity: 0.85, marginBottom: 20, color: colorTextoLegible(colorFondo) }}>
            {t("portal_ayuda_texto")}
          </p>

          <div
            style={{
              background: "#ffffff",
              borderRadius: estiloGlobal.cardRadius,
              boxShadow: estiloGlobal.cardShadow,
              border: estiloGlobal.cardBorder,
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#111827" }}>
              <Phone size={17} />
              <span>+505 2298 1000</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#111827" }}>
              <Globe size={17} />
              <a href="https://dsm.edu.ni" target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>
                dsm.edu.ni
              </a>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#111827" }}>
              <MapPin size={17} />
              <span>Carretera Sur Km 10 ½, Managua, Nicaragua</span>
            </div>
          </div>
        </div>

        <style>
          {`
            @media (min-width: 861px) {
              .portal-anclado {
                transform: translateX(-130px);
              }
            }
          `}
        </style>
      </Envolver>
    );

  if (vista === "colegio")
    return (
      <Envolver fondo={fondoActual}>
        <ProfesoresList
          servicioActual={servicioActual}
          fondoEfectivo={fondoActual}
          onVolver={volver}
          onSeleccionar={(p) => irA({ vista: "horarios", profesorSeleccionado: p })}
        />
      </Envolver>
    );


if (vista === "cda")
  return (
    <Envolver fondo={fondoActual}>
      <GradosCDA
        servicioActual={servicioActual}
        fondoEfectivo={fondoActual}
        onSeleccionarProfesor={(profesor) => irA({ vista: "horarios", profesorSeleccionado: profesor })}
      />
    </Envolver>
  );

  if (vista === "kindergarten")
    return (
      <Envolver fondo={fondoActual}>
        <KinderProfesoresList
          servicioActual={servicioActual}
          onVolver={volver}
          onSeleccionar={(p) => irA({ vista: "horarios", profesorSeleccionado: p })}
        />
      </Envolver>
    );

  if (vista === "horarios" && profesorSeleccionado)
    return (
      <Envolver fondo={fondoActual}>
        <HorariosProfesor
          profesor={profesorSeleccionado}
          servicioActual={servicioActual}
          fondoEfectivo={fondoActual}
          onVolver={volver}
          onSeleccionarHorario={(h) => irA({ vista: "reserva", horarioSeleccionado: h })}
        />
      </Envolver>
    );

  if (vista === "reserva" && profesorSeleccionado && horarioSeleccionado)
    return (
      <Envolver fondo={fondoActual}>
        <ReservaCita
          profesor={profesorSeleccionado}
          horarioSeleccionado={horarioSeleccionado}
          padre={usuario}
          servicioActual={servicioActual}
          onVolver={volver}
          onFinalizar={volverAListaProfesores}
          onVerMisCitas={() => irA({ vista: "misCitas", profesorSeleccionado: null, horarioSeleccionado: null })}
        />
      </Envolver>
    );

  if (vista === "misCitas")
    return (
      <Envolver fondo={colorFondo}>
        <MisCitasPadre usuario={usuario} onVolver={volver} fondoEfectivo={colorFondo} />
      </Envolver>
    );

  /* ================= SERVICIOS ================= */

  const colorTextoServicios = colorTextoLegible(colorFondo);

  return (
    <Envolver fondo={colorFondo}>
      <main style={{ flex: 1, padding: estiloGlobal.shell === "sidebar" ? "20px 0 50px" : "50px 20px", textAlign: "center" }}>
        <h2 style={{ fontSize: "28px", fontWeight: "800", marginBottom: "16px", color: colorTextoLegible(colorFondo) }}>
          {t("panel_seleccione_servicio")}
        </h2>

        {estiloGlobal.shell !== "sidebar" && (
          <button
            onClick={() => irA({ vista: "misCitas" })}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: colorTextoLegible(colorFondo) === "#ffffff" ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.08)",
              color: colorTextoLegible(colorFondo),
              border: `1px solid ${colorTextoLegible(colorFondo) === "#ffffff" ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.25)"}`,
              borderRadius: "999px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "700",
              cursor: "pointer",
              marginBottom: "30px",
            }}
          >
            {t("panel_ver_mis_citas")}
          </button>
        )}

        {cargandoServicios ? (
          <h3>{t("panel_cargando_servicios")}</h3>
        ) : estilo.layoutServicios === "tarjetas_iconos" ? (
          // ===== ESTILO "PORTAL LATERAL" — tarjetas grandes de 2 columnas =====
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: colorTextoServicios, textAlign: "left" }}>
              {t("portal_servicios_titulo")}
            </h3>
            <p style={{ fontSize: 13, opacity: 0.75, marginBottom: 20, color: colorTextoServicios, textAlign: "left" }}>
              {t("portal_servicios_sub")}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {servicios.map((s) => (
                <div
                  key={s.id}
                  onClick={() => manejarClickServicio(s)}
                  style={{
                    background: "#ffffff",
                    borderRadius: estilo.cardRadius,
                    boxShadow: estilo.cardShadow,
                    border: estilo.cardBorder,
                    padding: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    {s.imagen ? (
                      <img src={`${API}${s.imagen}`} alt={s.nombre} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                    ) : (
                      <span style={{ fontSize: 24 }}>📘</span>
                    )}
                  </div>

                  <h3 style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 }}>
                    {s.nombre}
                  </h3>

                  <ChevronRight size={20} color="#9ca3af" />
                </div>
              ))}
            </div>
          </div>
        ) : estilo.layoutServicios === "lista" ? (
          // ===== ESTILO "MODERNO" — servicios en lista de filas =====
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 480, margin: "0 auto", textAlign: "left" }}>
            {servicios.map((s) => (
              <div
                key={s.id}
                onClick={() => manejarClickServicio(s)}
                style={{
                  background: "#ffffff",
                  borderRadius: estilo.cardRadius,
                  boxShadow: estilo.cardShadow,
                  border: estilo.cardBorder,
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  {s.imagen ? (
                    <img src={`${API}${s.imagen}`} alt={s.nombre} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                  ) : (
                    <span style={{ fontSize: 22 }}>📘</span>
                  )}
                </div>

                <h3 style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 }}>
                  {s.nombre}
                </h3>

                <button
                  style={
                    estilo.botonEstilo === "outline"
                      ? {
                          background: "white",
                          color: s.color || "#2563eb",
                          border: `2px solid ${s.color || "#2563eb"}`,
                          borderRadius: estilo.botonRadius,
                          padding: "8px 16px",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          flexShrink: 0,
                        }
                      : {
                          background: armarFondo(s.color || "#2563eb", s.color_difuminado),
                          color: "white",
                          border: "none",
                          borderRadius: estilo.botonRadius,
                          padding: "8px 16px",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          flexShrink: 0,
                        }
                  }
                >
                  {t("panel_ingresar")}
                </button>
              </div>
            ))}
          </div>
        ) : (
          // ===== ESTILO "CLÁSICO" / OTROS — servicios en cuadrícula de tarjetas =====
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "25px" }}>
            {servicios.map((s) => (
              <div
                key={s.id}
                onClick={() => manejarClickServicio(s)}
                style={{
                  width: "230px",
                  borderRadius: "14px",
                  overflow: "hidden",
                  cursor: "pointer",
                  background: "#ffffff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0px)";
                }}
              >
                {/* IMAGEN */}
                <div
                  style={{
                    height: "140px",
                    background: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                  }}
                >
                  {s.imagen ? (
                    <img
                      src={`${API}${s.imagen}`}
                      alt={s.nombre}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: "40px" }}>
                      📘
                    </div>
                  )}
                </div>

                {/* CONTENIDO */}
                <div
                  style={{
                    padding: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "15px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                      lineHeight: "1.3",
                    }}
                  >
                    {s.nombre}
                  </h3>

                  {/* BOTÓN */}
                  <button
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "8px",
                      border: "none",
                      background: armarFondo(s.color || "#2563eb", s.color_difuminado),
                      color: "white",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    {t("panel_ingresar")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </Envolver>
  );
};

export default PadrePanel;